import { useEffect, useState } from "react";
import { auditApi, businessesApi } from "@/lib/api";
import { useAuth, isMaster } from "@/lib/auth";
import { Link } from "react-router-dom";
import { Search, RefreshCcw, FileText } from "lucide-react";

const ACTION_STYLES = {
  "document.upload": "bg-status-pending/10 text-status-pending",
  "document.ocr_complete": "bg-blue-100 text-blue-700",
  "document.edit": "bg-canvas-border text-espresso",
  "document.publish": "bg-status-synced/10 text-status-synced",
  "document.reassign": "bg-gold-soft text-gold",
  "document.mark_not_dup": "bg-canvas-border text-espresso-ink",
  "document.bulk_delete": "bg-status-duplicate/10 text-status-duplicate",
  "document.bulk_set_status": "bg-canvas-border text-espresso-ink",
  "accounts.sync": "bg-gold-soft text-gold",
};

const ACTION_LABELS = {
  "document.upload": "Uploaded",
  "document.ocr_complete": "AI extracted",
  "document.edit": "Edited",
  "document.publish": "Published",
  "document.reassign": "Reassigned",
  "document.mark_not_dup": "Un-flagged duplicate",
  "document.bulk_delete": "Bulk deleted",
  "document.bulk_set_status": "Bulk status change",
  "accounts.sync": "Chart of accounts synced",
};

const formatMeta = (row) => {
  const m = row.meta || {};
  if (row.action === "document.publish") return `${m.provider || ""} ${m.external_id ? "· " + String(m.external_id).slice(0, 12) : ""}`.trim();
  if (row.action === "document.reassign") return `→ ${m.to_business_id?.slice(0, 8) || ""}`;
  if (row.action === "document.edit") return (m.fields || []).join(", ");
  if (row.action === "document.bulk_set_status") return `${m.count || 0} docs → ${m.status || ""}`;
  if (row.action === "document.bulk_delete") return `${m.count || 0} docs`;
  if (row.action === "accounts.sync") return `${m.provider || ""} +${m.created || 0}/~${m.updated || 0}`;
  return "";
};

export default function AuditLog() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [actions, setActions] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [biz, setBiz] = useState("");
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = {};
    if (biz) params.business_id = biz;
    if (action) params.action = action;
    if (search) params.search = search;
    auditApi.list(params).then(setRows).finally(() => setLoading(false));
  };

  useEffect(() => {
    businessesApi.list().then(setBusinesses);
    auditApi.actions().then(setActions);
  }, []);
  useEffect(load, [biz, action]); // eslint-disable-line

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-4xl font-bold text-espresso" data-testid="audit-title">Audit log</h1>
        <p className="text-espresso-ink/60 mt-2">Every action across DocHub — who did what, when, and to which document.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {isMaster(user) && (
          <select value={biz} onChange={(e) => setBiz(e.target.value)} data-testid="audit-biz" className="px-3 py-2 bg-white border border-canvas-border text-sm brand-focus">
            <option value="">All businesses</option>
            {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action" className="px-3 py-2 bg-white border border-canvas-border text-sm brand-focus">
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
        </select>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-espresso-ink/40" />
          <input
            data-testid="audit-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search actor email, action, doc id"
            className="pl-9 pr-3 py-2 bg-white border border-canvas-border text-sm brand-focus w-80"
          />
        </div>
        <button onClick={load} data-testid="audit-refresh" className="inline-flex items-center gap-2 px-3 py-2 border border-canvas-border text-sm font-semibold text-espresso hover:bg-canvas-border/50">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="bg-white border border-canvas-border overflow-hidden shadow-soft">
        {loading && rows.length === 0 ? (
          <div className="p-14 text-center text-espresso-ink/50">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center text-espresso-ink/50" data-testid="audit-empty">No audit events match your filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-canvas text-xs uppercase tracking-wider text-espresso-ink/60">
              <tr>
                <th className="text-left px-5 py-3">When</th>
                <th className="text-left px-5 py-3">Who</th>
                <th className="text-left px-5 py-3">Action</th>
                <th className="text-left px-5 py-3">Document</th>
                <th className="text-left px-5 py-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-canvas-border align-top" data-testid={`audit-row-${r.id}`}>
                  <td className="px-5 py-3 whitespace-nowrap text-espresso-ink/70">{new Date(r.at).toLocaleString()}</td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-espresso">{r.actor_email || "system"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-espresso-ink/50">{r.actor_role || "-"}</div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2 py-1 rounded-md text-xs font-semibold ${ACTION_STYLES[r.action] || "bg-canvas-border text-espresso-ink"}`}>
                      {ACTION_LABELS[r.action] || r.action}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    {r.document_id ? (
                      <Link to={`/documents/${r.document_id}`} className="text-gold hover:text-gold-light font-mono text-xs inline-flex items-center gap-1" data-testid={`audit-doc-${r.id}`}>
                        <FileText className="w-3 h-3" /> {r.document_id.slice(0, 8)}…
                      </Link>
                    ) : (
                      <span className="text-espresso-ink/40">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-espresso-ink/80 text-xs">{formatMeta(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
